// src/dev/OnboardingInspector.tsx
// Main testing panel component for debugging onboarding
import { useContext } from "react";
import { useOnboardingInspector } from "./OnboardingInspectorContext";
import { useNetworkLogger } from "./hooks/useNetworkLogger";
import { OnboardingContext } from "@/contexts/onboarding-context";
import { OnboardingDataContext } from "@/contexts/onboarding-data-context";
import { useUser } from "@clerk/clerk-react";
import { useAccessLevel } from "@/hooks/use-access-level";
import { InspectorSection } from "./components/InspectorSection";
import {
  X,
  Copy,
  Download,
  Trash2,
  RefreshCw,
  CheckCircle,
  XCircle,
  Loader2,
  AlertTriangle,
  Play,
  Square,
} from "lucide-react";
import type { NetworkLogEntry, OnboardingTab } from "./types/inspector-types";

export function OnboardingInspector() {
  const {
    isOpen,
    setIsOpen,
    forceOnboarding,
    setForceOnboarding,
    simulationMode,
    setSimulationEnabled,
    setSimulatedEmail,
    clearSimulation,
    networkLogs,
    clearNetworkLogs,
    overrides,
    setStageOverride,
    setRunStateOverride,
    setDataOverride,
    setJourneyOverride,
    clearAllOverrides,
    localStorageState,
    clerkMetadataState,
    discrepancies,
    refreshStorageState,
    exportState,
  } = useOnboardingInspector();

  // Use context directly to avoid throwing when outside provider
  const onboardingCtx = useContext(OnboardingContext);
  const onboardingDataCtx = useContext(OnboardingDataContext);

  const currentStage = onboardingCtx?.stage;
  const onboardingData = onboardingDataCtx?.data;
  // webAppUrl comes from localStorage state, not context

  const { user } = useUser();
  const { isCustomer, isCommunityMember, hasUnlimitedAccess } = useAccessLevel();

  // Hook to intercept fetch calls for network logging
  useNetworkLogger();

  if (!isOpen) return null;

  // Infer run state from data
  const inferRunState = () => {
    if (!onboardingData) return "unknown";
    const hasAccountData =
      onboardingData.account && onboardingData.account.length > 0;
    const hasKeywordData =
      onboardingData.keyword80 && onboardingData.keyword80.length > 0;

    if (!hasAccountData && !hasKeywordData) return "never";
    return "has_data";
  };

  // Get user type
  const getUserType = () => {
    if (!user) return "anonymous";
    if (hasUnlimitedAccess) return isCommunityMember ? "community" : "customer";
    return "free";
  };

  // Copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Handle export
  const handleExport = () => {
    const json = exportState();
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `inspector-export-${new Date().toISOString().slice(0, 19)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed top-10 right-0 bottom-0 w-[400px] bg-slate-900 border-l border-slate-700 z-[99] flex flex-col text-slate-200 font-mono text-xs overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-slate-800 border-b border-slate-700">
        <h2 className="text-sm font-semibold text-slate-100">
          Onboarding Inspector
        </h2>
        <div className="flex items-center gap-1">
          <button
            onClick={handleExport}
            className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-slate-200"
            title="Export state"
          >
            <Download className="h-4 w-4" />
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-slate-200"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        {/* Force Onboarding - Most Important */}
        <InspectorSection
          title="Test Onboarding"
          badge={forceOnboarding ? "ACTIVE" : undefined}
          badgeColor="warning"
        >
          <div className="space-y-3">
            {/* Force Onboarding Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <div className="text-slate-300">Force Onboarding Mode</div>
                <div className="text-xs text-slate-500">Bypass redirect to /trends</div>
              </div>
              <button
                onClick={() => setForceOnboarding(!forceOnboarding)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium ${
                  forceOnboarding
                    ? "bg-amber-600 text-white"
                    : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                }`}
              >
                {forceOnboarding ? "ON" : "OFF"}
              </button>
            </div>

            {forceOnboarding && (
              <div className="bg-amber-900/30 border border-amber-700 rounded p-2 text-xs text-amber-200">
                <strong>Active:</strong> Homepage will redirect to /onboarding/start instead of /trends.
                <a
                  href="/"
                  className="block mt-1 text-amber-300 underline hover:text-amber-100"
                >
                  Go to homepage →
                </a>
              </div>
            )}
          </div>
        </InspectorSection>

        {/* Simulation Mode Section */}
        <InspectorSection
          title="Simulation Mode"
          badge={simulationMode.enabled ? "ON" : undefined}
          badgeColor="success"
        >
          <div className="space-y-3">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between">
              <span className="text-slate-300">Simulate New User</span>
              <button
                onClick={() => setSimulationEnabled(!simulationMode.enabled)}
                className={`flex items-center gap-1 px-3 py-1 rounded text-xs font-medium ${
                  simulationMode.enabled
                    ? "bg-emerald-600 text-white"
                    : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                }`}
              >
                {simulationMode.enabled ? (
                  <>
                    <Square className="h-3 w-3" /> Stop
                  </>
                ) : (
                  <>
                    <Play className="h-3 w-3" /> Start
                  </>
                )}
              </button>
            </div>

            {simulationMode.enabled && (
              <>
                {/* Simulated Email */}
                <div>
                  <div className="text-slate-400 text-xs mb-1">Simulated Email:</div>
                  <input
                    type="email"
                    value={simulationMode.simulatedEmail}
                    onChange={(e) => setSimulatedEmail(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-slate-200"
                    placeholder="test-user@example.com"
                  />
                </div>

                {/* Intercepted Clerk Metadata */}
                <div>
                  <div className="text-slate-400 text-xs mb-1">
                    Intercepted Clerk Writes:
                  </div>
                  <div className="bg-slate-800 rounded p-2 max-h-32 overflow-y-auto">
                    {Object.keys(simulationMode.interceptedMetadata).length === 0 ? (
                      <div className="text-slate-500 italic text-xs">
                        No Clerk writes intercepted yet
                      </div>
                    ) : (
                      <pre className="text-xs text-cyan-400 whitespace-pre-wrap">
                        {JSON.stringify(simulationMode.interceptedMetadata, null, 2)}
                      </pre>
                    )}
                  </div>
                </div>

                {/* Clear Simulation */}
                <button
                  onClick={clearSimulation}
                  className="w-full bg-red-900/50 hover:bg-red-900/70 text-red-200 py-1.5 rounded text-xs flex items-center justify-center gap-1"
                >
                  <Trash2 className="h-3 w-3" />
                  Clear Simulation & Reset
                </button>

                {/* Info */}
                <div className="bg-blue-900/30 border border-blue-700 rounded p-2 text-xs text-blue-200">
                  <strong>How it works:</strong> When enabled, Clerk metadata writes are intercepted
                  and shown here instead of being saved to your real account. Navigate to
                  /onboarding/start to test as a new user.
                </div>
              </>
            )}
          </div>
        </InspectorSection>

        {/* Current State Section */}
        <InspectorSection title="Current State">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-400">Stage:</span>
              <span className="text-cyan-400 font-medium">
                {overrides.stage.enabled ? (
                  <span className="text-amber-400">
                    {overrides.stage.stage} (override)
                  </span>
                ) : (
                  currentStage || "N/A"
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Run State:</span>
              <span className="text-cyan-400">{inferRunState()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">User Type:</span>
              <span className="text-cyan-400">{getUserType()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Signed In:</span>
              <span className={user ? "text-emerald-400" : "text-slate-500"}>
                {user ? "Yes" : "No"}
              </span>
            </div>
          </div>
        </InspectorSection>

        {/* Data Sources Section */}
        <InspectorSection title="Data Sources">
          <div className="space-y-2">
            <div>
              <div className="text-slate-400 mb-1">Web App URL:</div>
              <div className="flex items-center gap-1">
                <code className="flex-1 bg-slate-800 px-2 py-1 rounded text-xs break-all">
                  {localStorageState.temp_web_app_url || "Not set"}
                </code>
                {localStorageState.temp_web_app_url && (
                  <button
                    onClick={() =>
                      copyToClipboard(localStorageState.temp_web_app_url || "")
                    }
                    className="p-1 hover:bg-slate-700 rounded"
                    title="Copy"
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
            <div>
              <div className="text-slate-400 mb-1">Sheet URL:</div>
              <code className="block bg-slate-800 px-2 py-1 rounded text-xs break-all">
                {localStorageState.temp_sheet_url || "Not set"}
              </code>
            </div>
          </div>
        </InspectorSection>

        {/* Network Log Section */}
        <InspectorSection
          title="Network Log"
          badge={networkLogs.length}
          badgeColor={networkLogs.some((l) => l.status === "error") ? "error" : "default"}
        >
          <div className="space-y-1">
            {networkLogs.length === 0 ? (
              <div className="text-slate-500 italic">No requests logged</div>
            ) : (
              <>
                <div className="flex justify-end mb-2">
                  <button
                    onClick={clearNetworkLogs}
                    className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1"
                  >
                    <Trash2 className="h-3 w-3" /> Clear
                  </button>
                </div>
                {networkLogs.map((log) => (
                  <NetworkLogEntryDisplay key={log.id} log={log} />
                ))}
              </>
            )}
          </div>
        </InspectorSection>

        {/* Sample Data Section */}
        <InspectorSection title="Sample Data">
          <div className="space-y-2">
            {onboardingData ? (
              <>
                {(
                  [
                    "account",
                    "keyword80",
                    "search80",
                    "quality",
                    "on_performance",
                    "on_settings",
                  ] as const
                ).map((tab) => {
                  const data = (onboardingData as any)[tab];
                  const count = Array.isArray(data) ? data.length : 0;
                  return (
                    <div key={tab} className="flex justify-between items-center">
                      <span className="text-slate-400">{tab}:</span>
                      <span
                        className={
                          count > 0 ? "text-emerald-400" : "text-slate-500"
                        }
                      >
                        {count} rows
                      </span>
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="text-slate-500 italic">No data loaded</div>
            )}
          </div>
        </InspectorSection>

        {/* Storage Inspector Section */}
        <InspectorSection
          title="Storage Inspector"
          badge={discrepancies.length > 0 ? discrepancies.length : undefined}
          badgeColor="warning"
        >
          <div className="space-y-3">
            <div className="flex justify-end">
              <button
                onClick={refreshStorageState}
                className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1"
              >
                <RefreshCw className="h-3 w-3" /> Refresh
              </button>
            </div>

            {/* Discrepancies */}
            {discrepancies.length > 0 && (
              <div className="bg-amber-900/30 border border-amber-700 rounded p-2 space-y-1">
                <div className="flex items-center gap-1 text-amber-400 font-medium">
                  <AlertTriangle className="h-3 w-3" />
                  Discrepancies Found
                </div>
                {discrepancies.map((d, i) => (
                  <div key={i} className="text-xs text-amber-200">
                    {d.message}
                  </div>
                ))}
              </div>
            )}

            {/* localStorage */}
            <div>
              <div className="text-slate-400 font-medium mb-1">localStorage:</div>
              <div className="bg-slate-800 rounded p-2 space-y-1">
                {Object.entries(localStorageState).length === 0 ? (
                  <div className="text-slate-500 italic">Empty</div>
                ) : (
                  Object.entries(localStorageState).map(([key, value]) => (
                    <div key={key} className="flex justify-between gap-2">
                      <span className="text-slate-400 truncate">{key}:</span>
                      <span className="text-cyan-400 truncate max-w-[150px]">
                        {value ? (value.length > 30 ? value.slice(0, 30) + "..." : value) : "null"}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Clerk Metadata */}
            <div>
              <div className="text-slate-400 font-medium mb-1">
                Clerk Metadata:
              </div>
              <div className="bg-slate-800 rounded p-2 space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-400">onboardingStage:</span>
                  <span className="text-cyan-400">
                    {clerkMetadataState.onboardingStage || "null"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">pendingAccountSetup:</span>
                  <span
                    className={
                      clerkMetadataState.pendingAccountSetup
                        ? "text-emerald-400"
                        : "text-slate-500"
                    }
                  >
                    {clerkMetadataState.pendingAccountSetup ? "Yes" : "No"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">accounts:</span>
                  <span className="text-cyan-400">
                    {clerkMetadataState.accounts?.length || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </InspectorSection>

        {/* State Overrides Section */}
        <InspectorSection title="State Overrides" defaultOpen={false}>
          <div className="space-y-4">
            {/* Stage Override */}
            <div className="space-y-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={overrides.stage.enabled}
                  onChange={(e) =>
                    setStageOverride({
                      ...overrides.stage,
                      enabled: e.target.checked,
                    })
                  }
                  className="rounded bg-slate-700 border-slate-600"
                />
                <span className="text-slate-300">Override Stage</span>
              </label>
              {overrides.stage.enabled && (
                <select
                  value={overrides.stage.stage}
                  onChange={(e) =>
                    setStageOverride({
                      ...overrides.stage,
                      stage: e.target.value as any,
                    })
                  }
                  className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs"
                >
                  <option value="START">START</option>
                  <option value="ANALYSIS">ANALYSIS</option>
                  <option value="CHOOSE">CHOOSE</option>
                  <option value="RESULTS">RESULTS</option>
                </select>
              )}
            </div>

            {/* Run State Override */}
            <div className="space-y-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={overrides.runState.enabled}
                  onChange={(e) =>
                    setRunStateOverride({
                      ...overrides.runState,
                      enabled: e.target.checked,
                    })
                  }
                  className="rounded bg-slate-700 border-slate-600"
                />
                <span className="text-slate-300">Override Run State</span>
              </label>
              {overrides.runState.enabled && (
                <select
                  value={overrides.runState.runState}
                  onChange={(e) =>
                    setRunStateOverride({
                      ...overrides.runState,
                      runState: e.target.value as any,
                    })
                  }
                  className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs"
                >
                  <option value="never">Never Run (no data)</option>
                  <option value="first">First Run (lite data)</option>
                  <option value="second">Second Run</option>
                  <option value="many">Many Runs (10+)</option>
                </select>
              )}
            </div>

            {/* Data Override */}
            <div className="space-y-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={overrides.data.enabled}
                  onChange={(e) =>
                    setDataOverride({
                      ...overrides.data,
                      enabled: e.target.checked,
                    })
                  }
                  className="rounded bg-slate-700 border-slate-600"
                />
                <span className="text-slate-300">Override Data State</span>
              </label>
              {overrides.data.enabled && (
                <select
                  value={overrides.data.mode}
                  onChange={(e) =>
                    setDataOverride({
                      ...overrides.data,
                      mode: e.target.value as any,
                    })
                  }
                  className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs"
                >
                  <option value="complete">Complete (all tabs)</option>
                  <option value="partial">Partial (missing tabs)</option>
                  <option value="empty">Empty (no data)</option>
                  <option value="error">Error (fetch fails)</option>
                </select>
              )}
            </div>

            {/* Journey Override */}
            <div className="space-y-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={overrides.journey.enabled}
                  onChange={(e) =>
                    setJourneyOverride({
                      ...overrides.journey,
                      enabled: e.target.checked,
                    })
                  }
                  className="rounded bg-slate-700 border-slate-600"
                />
                <span className="text-slate-300">Override Journey State</span>
              </label>
              {overrides.journey.enabled && (
                <select
                  value={overrides.journey.state}
                  onChange={(e) =>
                    setJourneyOverride({
                      ...overrides.journey,
                      state: e.target.value as any,
                    })
                  }
                  className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs"
                >
                  <option value="new">New User (first visit)</option>
                  <option value="mid_exit">Mid-Exit (has localStorage, no account)</option>
                  <option value="free_with_data">Free + Prior Data</option>
                  <option value="signup_incomplete">Signup Incomplete</option>
                </select>
              )}
            </div>

            {/* Clear All */}
            <button
              onClick={clearAllOverrides}
              className="w-full bg-slate-700 hover:bg-slate-600 text-slate-200 py-1.5 rounded text-xs flex items-center justify-center gap-1"
            >
              <Trash2 className="h-3 w-3" />
              Clear All Overrides
            </button>
          </div>
        </InspectorSection>
      </div>
    </div>
  );
}

// Network log entry display
function NetworkLogEntryDisplay({ log }: { log: NetworkLogEntry }) {
  return (
    <div className="flex items-center gap-2 py-1 px-2 bg-slate-800 rounded text-xs">
      {log.status === "pending" && (
        <Loader2 className="h-3 w-3 text-cyan-400 animate-spin" />
      )}
      {log.status === "success" && (
        <CheckCircle className="h-3 w-3 text-emerald-400" />
      )}
      {log.status === "error" && (
        <XCircle className="h-3 w-3 text-red-400" />
      )}
      <span className="text-slate-300 truncate flex-1">
        {log.tab || "unknown"}
      </span>
      {log.rowCount !== undefined && (
        <span className="text-slate-500">{log.rowCount} rows</span>
      )}
      {log.duration !== undefined && (
        <span className="text-slate-500">{Math.round(log.duration)}ms</span>
      )}
    </div>
  );
}
